import React from "react";
import type {
  CanvasDataSource,
  CanvasElement,
  CanvasGridConfig,
} from "../types";
import {
  CanvasDataWidgetRenderer,
  CanvasRenderer,
  CanvasTimelineRenderer,
  CanvasViewGridRenderer,
  EmbeddedCanvasRenderer,
} from "../components/canvasBuilder";
import type { RenderableElement } from "../components/canvasBuilder";
import { CanvasRuntimeProvider } from "../components/canvasBuilder/CanvasRuntimeContext";
import { CanvasWidgetDataContext } from "../components/canvasBuilder/elements/CanvasAggregateWidget";
import { CanvasThemeScope } from "../theme/CanvasThemeScope";
import type { BreakpointKind } from "../responsive/breakpoints";

interface CanvasBuilderLivePreviewProps {
  elements: RenderableElement[];
  config: CanvasGridConfig;
  workspaceId: string;
  canvasId: string | null;
  dataSources: CanvasDataSource[];
  title: string;
  description?: string;
  breakpoint: BreakpointKind;
  themeConfig?: unknown;
  color?: string;
}

function toCanvasElement(element: RenderableElement): CanvasElement {
  return {
    id: element.id,
    type: element.type as CanvasElement["type"],
    props: element.props,
    data_binding: element.data_binding as CanvasElement["data_binding"],
    layout: {
      gridColumn: element.gridColumn,
      gridRow: element.gridRow,
      columnSpan: element.columnSpan,
      rowSpan: element.rowSpan,
    },
    parentElementId: element.parentElementId,
    parentPanelId: element.parentPanelId,
    displayOrder: element.displayOrder,
  };
}

export function CanvasBuilderLivePreview({
  elements,
  config,
  workspaceId,
  canvasId,
  dataSources,
  title,
  description,
  breakpoint,
  themeConfig,
  color,
}: CanvasBuilderLivePreviewProps) {
  const readOnlyElements = elements.map((element) => ({
    ...element,
    effectiveAccess: "read" as const,
    props: {
      ...element.props,
      allow_create: false,
      allow_edit: false,
      allow_delete: false,
      related_records_config: element.props.related_records_config
        ? {
            ...(element.props.related_records_config as Record<
              string,
              unknown
            >),
            allow_create: false,
            allow_edit: false,
          }
        : undefined,
    },
  }));

  return (
    <CanvasRuntimeProvider
      value={{
        workspaceId,
        canvasId,
        rowId: null,
        rowData: null,
        dataSources,
        canEdit: false,
      }}
    >
      <CanvasWidgetDataContext.Provider value={dataSources}>
        <CanvasThemeScope themeConfig={themeConfig} color={color}>
          <CanvasRenderer
            elements={readOnlyElements}
            config={config}
            mode="runtime"
            fieldState={{}}
            title={title}
            description={description}
            forcedBreakpoint={breakpoint}
            renderViewGrid={(element) => (
              <CanvasViewGridRenderer
                element={element}
                dataSources={dataSources}
                canvasId={canvasId}
              />
            )}
            renderTimeline={(element) => (
              <CanvasTimelineRenderer
                element={element}
                dataSources={dataSources}
                canvasId={canvasId}
              />
            )}
            renderDataWidget={(element) => (
              <CanvasDataWidgetRenderer
                element={element}
                dataSources={dataSources}
                canvasId={canvasId}
              />
            )}
            renderEmbeddedCanvas={(element) => (
              <EmbeddedCanvasRenderer
                element={toCanvasElement(element)}
                parentData={{}}
                workspaceId={workspaceId}
                parentCanvasId={canvasId || undefined}
                allowCreate={false}
                allowEdit={false}
                allowDelete={false}
              />
            )}
          />
        </CanvasThemeScope>
      </CanvasWidgetDataContext.Provider>
    </CanvasRuntimeProvider>
  );
}
